import React, { useEffect, useState } from "react";

import {
  Ri360CarrierCalibration,
  Ri360HotelCalibration,
} from "../../../../Model/StorageConfig";
import CalibrationApi from "../../../../api/CalibrationApi";
import ToolsApi from "../../../../api/ToolsApi";

import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import { getModules } from "../../../../redux/reducers/ModuleReducer";
import { getModuleStateById } from "../../../../redux/reducers/ModuleStateReducer";

import HotelOffset from "./HotelOffset";
import VectorCoordinate from "./VectorCoordinate";
import ArmMover from "../SlotCorrection/ArmMover";
import CommonDropDown from "../../../Common/DropDown/CommonDropDown";

import { EModuleType } from "../../../../Model/EModuleType";
import {
  CartesianVector,
  EArmType,
  EWaypointDirection,
  ModuleWaypoint,
  CartesianWaypoint,
} from "../../../../Model/Waypoint";
import { ECarrier } from "../../../../Model/ECarrier";
import { Ri360AutoCalibrationState } from "../../../../Model/Calibration";
import { EAutoCalibrationResult } from "../../../../Model/EAutoCalibrationResult";

import WaypointUtil from "../../../../Utility/WaypointUtil";
import AutoCalibrationUtil from "../../../../Utility/AutoCalibrationUtil";

import styles from "./HotelOffsetContent.module.css";
import { showToast } from "../../../../redux/actions/NotificationAction";
import { useDispatch } from "react-redux";

const ARM_CARRIER_WAYPOINT_SELECTION =
  "HOTEL_OFFSET_ARM_CARRIER_WAYPOINT_SELECTION";

interface ArmCarrierSelection {
  carrier?: ECarrier;
  hotelNumber?: number;
}

function getSelection(): ArmCarrierSelection {
  const value = localStorage.getItem(ARM_CARRIER_WAYPOINT_SELECTION);
  if (value != null) {
    return JSON.parse(value) as ArmCarrierSelection;
  } else {
    return {};
  }
}

function setSelection(selection: ArmCarrierSelection) {
  localStorage.setItem(
    ARM_CARRIER_WAYPOINT_SELECTION,
    JSON.stringify(selection)
  );
}

type Props = {
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  isCalibrationUpdated: boolean;
  setIsCalibrationUpdated: (isCalibrationUpdated: boolean) => void;
};

export default function HotelOffsetContent({
  setOnApply,
  setOnCancel,
  isCalibrationUpdated,
  setIsCalibrationUpdated,
}: Props) {
  const dispatch = useDispatch();
  const psrModule = useTypedSelector(
    (state) => getModules(state, EModuleType.PSR)[0]
  );

  const psrState = useTypedSelector(
    (state) => getModuleStateById(state)[psrModule.identifier].psr
  );

  const [carriers, setCarriers] = useState([] as ECarrier[]);
  const [carrier, setCarrier] = useState(ECarrier.Unknown);
  const [hotelWaypoint, setHotelWaypoint] = useState(
    null as CartesianWaypoint | null
  );
  const [carrierCalibrations, setCarrierCalibrations] = useState(
    [] as Ri360CarrierCalibration[]
  );
  const [carrierCalibration, setCarrierCalibration] = useState(
    null as Ri360CarrierCalibration | null
  );
  const [hotels, setHotels] = useState([] as Ri360HotelCalibration[]);
  const [hotel, setHotel] = useState(null as Ri360HotelCalibration | null);
  const [selectedVector, setSelectedVector] = useState(
    null as CartesianVector | null
  );

  const selection = getSelection();
  const [autoCalibrationResults, setAutoCalibrationResults] = useState(
    [] as Ri360AutoCalibrationState[]
  );

  const fetchData = async () => {
    const autoCalResults = await CalibrationApi.GetAutoCalibrationResult();
    const storageCalConfig = await CalibrationApi.getStorageCalibration();
    setAutoCalibrationResults(autoCalResults);
    setCarrierCalibrations(storageCalConfig.ri360StorageCalibrations);
    const carrierManualCalibration = autoCalResults
      .filter((autoCalResult) => autoCalResult.isOverriden)
      .map((autoCalResult) => autoCalResult.carrier);
    updateModuleWaypoints(
      storageCalConfig.ri360StorageCalibrations,
      carrierManualCalibration
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateModuleWaypoints = (
    storageCalibrations: Ri360CarrierCalibration[],
    carrierManualCalibrations: ECarrier[]
  ) => {
    CalibrationApi.getWaypoint().then((moduleWaypoints) => {
      const carriers = WaypointUtil.getCarriers(
        moduleWaypoints,
        psrModule.identifier,
        EArmType.PsrArm
      ).filter((c) => c != ECarrier.Imager);

      updateCarriers(carriers, storageCalibrations, carrierManualCalibrations);
    });
  };

  const updateCarriers = (
    carriers: ECarrier[],
    carrierCalibrations: Ri360CarrierCalibration[],
    carrierManualCalibrations: ECarrier[]
  ) => {
    let selectedCarrier =
      selection.carrier != null && carriers.includes(selection.carrier)
        ? selection.carrier
        : carriers[0];

    //filter only manual calibration result
    if (
      !(selectedCarrier in carrierManualCalibrations) &&
      carrierManualCalibrations.length > 0
    ) {
      selectedCarrier = carrierManualCalibrations[0];
    }

    setCarriers(carriers);
    setCarrier(selectedCarrier);
    const carrierCalibration = carrierCalibrations.filter(
      (s) => s.carrier === selectedCarrier
    )[0];
    setCarrierCalibration(carrierCalibration);
    const hotels = WaypointUtil.getRi360Hotels(
      carrierCalibrations,
      selectedCarrier
    );
    updateHotels(hotels, carrierCalibration);
  };

  const updateHotels = (
    hotels: Ri360HotelCalibration[],
    carrierCalibration: Ri360CarrierCalibration
  ) => {
    setHotels(hotels);

    const selectedHotel =
      selection.hotelNumber != null &&
      hotels.filter((hotel) => hotel.number === selection.hotelNumber).length >
        0
        ? hotels.filter((hotel) => hotel.number === selection.hotelNumber)[0]
        : hotels[0];

    updateHotel(carrierCalibration, selectedHotel);
  };

  const updateHotel = (
    carrierCalibration: Ri360CarrierCalibration,
    hotel: Ri360HotelCalibration
  ) => {
    setHotel(hotel);

    if (carrierCalibration != null) {
      CalibrationApi.GetWaypointForHotelWithOverride(
        psrModule.identifier,
        hotel.retrievePlateVector,
        carrierCalibration.carrier,
        hotel.number,
        {
          ri360StorageCalibrations: [carrierCalibration],
          ri1000StorageCalibrations: [],
        }
      ).then((moduleWaypoints) => {
        const hotelWaypoints: CartesianWaypoint[] =
          moduleWaypoints[0].cartesianWaypoints ?? [];
        const selectedWaypoint = hotelWaypoints[0];
        setHotelWaypoint(selectedWaypoint);
      });
    }
  };

  const onCarrierChanged = (carrier: ECarrier) => {
    setSelection({
      ...selection,
      carrier: carrier,
    });
    setCarrier(carrier);
    const carrierCalibration = carrierCalibrations.filter(
      (s) => s.carrier === carrier
    )[0];
    setCarrierCalibration(carrierCalibration);
    updateHotels(carrierCalibration.hotelCalibrations, carrierCalibration);
  };

  const onHotelChanged = (hotel: Ri360HotelCalibration) => {
    setSelection({
      ...selection,
      hotelNumber: hotel.number,
    });
    setHotel(hotel);
    if (hotel != null && hotelWaypoint != null && carrierCalibration != null) {
      CalibrationApi.GetWaypointForHotelWithOverride(
        psrModule.identifier,
        hotel.retrievePlateVector,
        carrier,
        hotel.number,
        {
          ri360StorageCalibrations: [carrierCalibration],
          ri1000StorageCalibrations: [],
        }
      ).then((moduleWaypoints) => {
        const hotelWaypoints = moduleWaypoints[0].cartesianWaypoints ?? [];
        setHotelWaypoint(hotelWaypoints[0]);
      });
    }
  };

  const onStorageCalibrationChanged = (
    carrierCalibration: Ri360CarrierCalibration
  ) => {
    setCarrierCalibrations(
      carrierCalibrations
        .filter((c) => c.carrier != carrierCalibration.carrier)
        .concat(carrierCalibration)
    );
    setCarrierCalibration(carrierCalibration);
    setIsCalibrationUpdated(true);
    if (hotel != null && hotelWaypoint != null) {
      CalibrationApi.GetWaypointForHotelWithOverride(
        psrModule.identifier,
        hotel.retrievePlateVector,
        carrier,
        hotel.number,
        {
          ri360StorageCalibrations: [carrierCalibration],
          ri1000StorageCalibrations: [],
        }
      ).then((moduleWaypoints) => {
        const hotelWaypoints = moduleWaypoints[0].cartesianWaypoints ?? [];
        setHotelWaypoint(hotelWaypoints[0]);
      });
    }
  };

  const onApply = () => {
    CalibrationApi.updateStorageCalibration({
      ri360StorageCalibrations: carrierCalibrations,
      ri1000StorageCalibrations: [],
    }).then(() => {
      setIsCalibrationUpdated(false);
      dispatch(
        showToast({
          duration: 2000,
          message: "The changes has been saved successfully",
          variant: "success",
        })
      );
    });
  };

  const revertChanges = () => {
    fetchData().then(() => {
      setIsCalibrationUpdated(false);
    });
  };

  useEffect(() => {
    setOnApply({ execute: onApply, disabled: !isCalibrationUpdated });
    setOnCancel({ execute: revertChanges, disabled: !isCalibrationUpdated });
  }, [isCalibrationUpdated, carrierCalibrations]);

  const carrierDisplayValues = carriers.map((carrier) => {
    const autoCalibrationResult = autoCalibrationResults.find(
      (result) => result.carrier === carrier
    );
    const isManualCalibrated = autoCalibrationResult
      ? autoCalibrationResult.isOverriden
      : true;
    return {
      display: `${AutoCalibrationUtil.getCarrierName(carrier)} (${
        isManualCalibrated ? "Manual Calibrated" : "Auto Calibrated"
      })`,
      value: carrier.toString(),
      disabled: !isManualCalibrated,
    };
  });

  const onMove = (vector: CartesianVector) => {
    if (hotelWaypoint == null) return;

    CalibrationApi.runWaypoint(psrModule.identifier, {
      cartesianWaypoint: {
        ...hotelWaypoint,
        vectors: [vector],
      },
      direction: EWaypointDirection.Forward,
    });
  };

  const onRetrieve = () => {
    if (carrierCalibration != null && hotel != null && hotelWaypoint != null) {
      const moduleWaypoint: ModuleWaypoint = {
        moduleIdentifier: carrierCalibration.moduleIdentifier,
        cartesianWaypoints: [hotelWaypoint],
      };
      ToolsApi.retrievePlateWithOverrideWaypoint(
        carrierCalibration?.moduleIdentifier,
        hotel.number,
        1,
        EAutoCalibrationResult.CurrentAutoCalibration,
        moduleWaypoint
      );
    }
  };

  const onStore = () => {
    if (carrierCalibration != null && hotel != null && hotelWaypoint != null) {
      const moduleWaypoint: ModuleWaypoint = {
        moduleIdentifier: carrierCalibration.moduleIdentifier,
        cartesianWaypoints: [hotelWaypoint],
      };
      ToolsApi.storePlateWithOverrideWaypoint(
        carrierCalibration.moduleIdentifier,
        hotel.number,
        1,
        EAutoCalibrationResult.CurrentAutoCalibration,
        moduleWaypoint
      );
    }
  };

  const onSet = () => {
    if (
      carrierCalibration == null ||
      hotel == null ||
      selectedVector == null ||
      psrState == null
    )
      return;

    const psrCoordinate = psrState.cartesianPosition;
    const selectedCoordinate = selectedVector.coordinate;

    const hotelIndex = carrierCalibration.hotelCalibrations.findIndex(
      (h) => h.number === hotel.number
    );
    let hotelCalibrations = [...carrierCalibration.hotelCalibrations];

    hotelCalibrations[hotelIndex].offsetX +=
      psrCoordinate.x - selectedCoordinate.x;
    hotelCalibrations[hotelIndex].offsetY +=
      psrCoordinate.y - selectedCoordinate.y;
    hotelCalibrations[hotelIndex].offsetZ +=
      psrCoordinate.z - selectedCoordinate.z;

    onStorageCalibrationChanged({
      ...carrierCalibration,
      hotelCalibrations: hotelCalibrations,
    });
  };

  const onClearSelectedIndex = () => {
    if (carrierCalibration == null || hotel == null) return;

    const hotelIndex = carrierCalibration.hotelCalibrations.findIndex(
      (h) => h.number === hotel.number
    );
    let hotelCalibrations = [...carrierCalibration.hotelCalibrations];
    hotelCalibrations[hotelIndex].offsetX = 0;
    hotelCalibrations[hotelIndex].offsetY = 0;
    hotelCalibrations[hotelIndex].offsetZ = 0;

    onStorageCalibrationChanged({
      ...carrierCalibration,
      hotelCalibrations: hotelCalibrations,
    });
  };

  const onClearAll = () => {
    if (carrierCalibration == null) return;

    const hotelCalibrations = carrierCalibration.hotelCalibrations.map(
      (hotel) => ({
        ...hotel,
        offsetX: 0,
        offsetY: 0,
        offsetZ: 0,
      })
    );

    onStorageCalibrationChanged({
      ...carrierCalibration,
      hotelCalibrations: hotelCalibrations,
    });
  };

  if (carrierCalibration == null || hotelWaypoint == null) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.topContent}>
          <label>Hotel Carrier</label>
          <span>:</span>
          <CommonDropDown
            id={"hotel-cal-carrier-sel"}
            dropDownDisplayValues={carrierDisplayValues}
            value={carrier.toString()}
            onChange={(carrierName) =>
              onCarrierChanged(Number.parseInt(carrierName))
            }
            style={{
              width: 330,
            }}
          />
        </div>
        <div className={styles.bottomContent}>
          <HotelOffset
            storageCalibration={carrierCalibration}
            setStorageCalibration={onStorageCalibrationChanged}
            selectedHotelNumber={hotel?.number ?? 1}
            setSelectedHotelNumber={(hotelNumber) =>
              onHotelChanged(hotels.filter((h) => h.number == hotelNumber)[0])
            }
            canSet={selectedVector != null}
            onSet={onSet}
            onClearAll={onClearAll}
            onClearSelectedIndex={onClearSelectedIndex}
            onReset={revertChanges}
          />
          <VectorCoordinate
            cartesianVectors={hotelWaypoint.vectors}
            onRetrieve={onRetrieve}
            onStore={onStore}
            onMove={onMove}
            setSelectedVector={setSelectedVector}
          />
        </div>
      </div>
      <aside className={styles.sidebar}>
        <ArmMover />
      </aside>
    </div>
  );
}
